import * as focusTrap from 'focus-trap';

type AppHeader = {
  element: HTMLElement | null;
  body: HTMLBodyElement | null;
  overlay: HTMLElement | null;
  mobileMenuBuffer: HTMLElement | null;
  init: () => void;
  closeAll: () => void;
  updateScrollLock: () => void;
  listenToIntersection: () => void;

  menuWrapperToggle: () => void;
  menuWrapperOpen: () => void;
  menuWrapperClose: () => void;
  menuWrapper: HTMLElement | null;
  menuWrapperToggleButton: HTMLElement | null;
  menuFocusTrap: focusTrap.FocusTrap | null;

  subnavToggle: (element: HTMLElement) => void;
  subnavOpen: (element: HTMLElement) => void;
  subnavClose: (element: HTMLElement) => void;
  subnavCloseAll: () => void;
  subnavToggleButtons: NodeListOf<HTMLElement> | null;
  subnavFocusTrap: focusTrap.FocusTrap | null;
};

const AppHeader: AppHeader = {
  element: document.querySelector('[data-hook=appHeader]'),
  body: document.querySelector('body'),
  overlay: document.querySelector('[data-hook=appHeader__primaryNavOverlay]'),
  mobileMenuBuffer: document.querySelector('[data-hook=appHeader__mobileMenuWrapperBuffer]'),

  menuWrapper: null,
  menuWrapperToggleButton: null,
  menuFocusTrap: null,

  subnavToggleButtons: null,
  subnavFocusTrap: null,

  init() {
    if (!this.element) return;

    this.listenToIntersection();

    this.closeAll(); // Ensure nav gets closed on page load to fight bfcache

    this.menuWrapper = document.querySelector('[data-hook=appHeader__menuWrapper]');

    this.menuWrapperToggleButton = document.querySelector(
      '[data-hook=appHeader__menuWrapperToggleButton]',
    );

    this.subnavToggleButtons = document.querySelectorAll(
      '[data-hook=appHeader__subnavToggleButton]',
    );

    this.mobileMenuBuffer?.addEventListener('click', () => {
      this.closeAll();
    });

    this.overlay?.addEventListener('click', () => {
      this.closeAll();
    });

    this.menuWrapperToggleButton?.addEventListener('click', () => {
      this.menuWrapperToggle();
    });

    this.subnavToggleButtons?.forEach((element) => {
      element.addEventListener('click', () => {
        this.subnavToggle(element);
      });
    });

    // Allow other bundles to close the mobile menu without reaching into this singleton.
    // The React GlobalSearchModal (in the separate `app-react` bundle) dispatches this on
    // the document after a search is submitted from within the open mobile menu — on the
    // global search page that submit is a same-page hash change with no full reload to clear
    // the menu. closeAll() only ever closes (and is a no-op when nothing is open), so a stray
    // event is harmless.
    document.addEventListener('appHeader:closeAll', () => {
      this.closeAll();
    });
  },

  closeAll() {
    this.subnavCloseAll();
    this.menuWrapperClose();
  },

  // Lock background page scroll while any menu is open, by toggling the shared `has-overlay`
  // body hook (see app.scss). Recomputed from live DOM state so it stays correct no matter which
  // open/close path ran. Pairs with `html { scrollbar-gutter: stable }` to avoid layout shift.
  updateScrollLock() {
    const anySubnavOpen = Array.from(this.subnavToggleButtons ?? []).some(
      (button) => button.getAttribute('aria-expanded') === 'true',
    );
    const mobileMenuOpen = this.menuWrapper?.classList.contains('is-open') ?? false;
    this.body?.classList.toggle('has-overlay', anySubnavOpen || mobileMenuOpen);
  },

  menuWrapperToggle() {
    if (this.menuWrapper?.classList.contains('is-open')) {
      this.closeAll();
    } else {
      this.menuWrapperOpen();
    }
  },

  menuWrapperOpen() {
    if (!this.element) return;

    this.menuWrapper?.classList.add('is-open');
    this.menuWrapperToggleButton?.setAttribute('aria-expanded', 'true');

    if (this.element) {
      this.menuFocusTrap = focusTrap.createFocusTrap(this.element, {
        clickOutsideDeactivates: true,
      });
      this.menuFocusTrap?.activate();
    }

    this.updateScrollLock();
  },

  menuWrapperClose() {
    if (!this.element) return;

    this.menuWrapper?.classList.remove('is-open');
    this.menuWrapperToggleButton?.setAttribute('aria-expanded', 'false');

    this.menuFocusTrap?.deactivate();

    this.updateScrollLock();
  },

  subnavToggle(element: HTMLElement) {
    const subnavPanel = element?.nextElementSibling;
    if (!subnavPanel) return;

    if (element.matches('[aria-expanded="false"]')) {
      // Close them all
      this.subnavToggleButtons?.forEach((toggleSubnavButton) => {
        toggleSubnavButton.setAttribute('aria-expanded', 'false');
      });

      this.subnavOpen(element);
    } else {
      this.subnavClose(element);
    }
  },

  subnavOpen(element: HTMLElement) {
    element?.setAttribute('aria-expanded', 'true');
    const parent = element.closest('li') as HTMLElement;

    if (this.element) {
      this.subnavFocusTrap = focusTrap.createFocusTrap(parent, {
        clickOutsideDeactivates: true,
      });
      this.subnavFocusTrap?.activate();
    }

    this.updateScrollLock();
  },

  subnavClose(element: HTMLElement) {
    element?.setAttribute('aria-expanded', 'false');
    this.subnavFocusTrap?.deactivate();

    this.updateScrollLock();
  },

  subnavCloseAll() {
    // Loop over all subnav items and ensure they are closed
    this.subnavToggleButtons?.forEach((subnavToggleButton) => {
      if (subnavToggleButton.getAttribute('aria-expanded') === 'true') {
        this.subnavClose(subnavToggleButton);
      }
    });

    this.updateScrollLock();
  },

  listenToIntersection() {
    let isDebouncing = false;
    const debounce = 250; // in ms
    const buffer = 15; // in px
    const observerOptions = {
      trackVisibility: true,
      delay: 100,
      threshold: [1],
    };

    /**
     * Callback for our observer.
     * @returns void
     */
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];

      // Will settle at -1 when fully stuck
      const entryTopRelativeToViewport = entry.boundingClientRect.y;
      const entryHeight = entry.boundingClientRect.height;

      // The threshold in-which we consider the element to be "stuck"
      const stuckThreshold = Math.floor(entryHeight + buffer);

      // Only apply stuck when the entries position from the top is under the threshold we set.
      // We're not looking for -1, because it'll technically not reach it if we adjust the height with css, that's one of the causes the jittering effect.
      const scrolledWithinThreshold = entryTopRelativeToViewport < stuckThreshold;

      // Head is stuck to the top if the intersector is less than one and
      if (entry.intersectionRatio < 1 && scrolledWithinThreshold && !isDebouncing) {
        isDebouncing = true;
        this.element?.classList.add('is-scrolled');

        // We're not stuck, but don't change it if we're in a debounce period.
      } else if (!isDebouncing) {
        isDebouncing = true;
        this.element?.classList.remove('is-scrolled');
      }

      // Reset the debounce.
      setTimeout(() => {
        isDebouncing = false;
      }, debounce);
    };

    // Setup a new observer.
    const observer = new window.IntersectionObserver(handleIntersection, observerOptions);

    const appHeaderIntersector = document.querySelector(
      '[data-scroll-intersector="appHeaderIntersector"]',
    );
    if (appHeaderIntersector) {
      observer.observe(appHeaderIntersector);
    }
  },
};

export default AppHeader;
